import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Leaf, Users, Shield, Film, Sparkles, Target, ArrowRight } from 'lucide-react';

const engagements = [
  {
    index: '01',
    icon: Leaf,
    title: "Engagement environnemental",
    description: "Face a l'urgence ecologique, nous croyons au pouvoir du cinema pour eveiller les consciences et nourrir un dialogue constructif autour des grands enjeux de notre epoque. En soutenant ces recits engages, nous offrons aux mecenes l'opportunite d'inscrire leur engagement environnemental dans des projets culturels porteurs de sens."
  },
  {
    index: '02',
    icon: Users,
    title: "Impact social & societal",
    description: "Nous accompagnons des projets qui mettent en lumiere des recits porteurs d'inclusion, d'egalite des chances et de diversite. A travers la representation de parcours singuliers, de minorites, de questions de genre ou de fractures sociales, ces oeuvres participent a une meilleure comprehension des enjeux sociaux actuels."
  },
  {
    index: '03',
    icon: Shield,
    title: "Gouvernance transparente",
    description: "Nous inscrivons chaque projet dans un cadre de gestion responsable, permettant aux mecenes de s'engager en toute confiance. A travers une gouvernance transparente, un suivi rigoureux des financements et des indicateurs partages, nous garantissons un pilotage ethique et professionnel."
  }
];

const youthStats = [
  { value: "80%", label: "de seances pour les jeunes" },
  { value: "24 ans", label: "l'age moyen des reals avec lesquels nous travaillons" }
];

export default function ConvictionPage() {
  const heroRef = useRef(null);
  const missionRef = useRef(null);
  const engagementsRef = useRef(null);
  const youthRef = useRef(null);
  const manifestoRef = useRef(null);

  const isHeroInView = useInView(heroRef, { once: true });
  const isMissionInView = useInView(missionRef, { once: true, margin: '-100px' });
  const isEngagementsInView = useInView(engagementsRef, { once: true, margin: '-100px' });
  const isYouthInView = useInView(youthRef, { once: true, margin: '-100px' });
  const isManifestoInView = useInView(manifestoRef, { once: true, margin: '-100px' });

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden" ref={heroRef}>
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-transparent to-[#050505] z-10" />
          <div className="absolute inset-0 bg-[url('https://customer-assets.emergentagent.com/job_cinema-mission/artifacts/mheeqto4_cinema-enfant-bordeaux.jpe')] bg-cover bg-center opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-transparent to-[#050505]" />
        </div>
        
        {/* Animated particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-[#D4AF37]/30 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -100, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>

        <div className="container-custom relative z-20 text-center py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <span className="overline mb-6 block">Notre ADN</span>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-medium mb-6 leading-tight">
              <span className="text-white">Conviction</span>
              <span className="text-[#71717a] font-normal mx-2">&</span>
              <span className="text-gradient">Engagement</span>
            </h1>
            <p className="text-[#a1a1aa] text-lg md:text-xl max-w-2xl mx-auto">
              Le cinema n'est pas un divertissement. C'est un acte.
              <br />
              Une arme. Une lumiere.
            </p>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-white/20 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-[#D4AF37] rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Section 1: Message Culturel Fort */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        {/* Background glow */}
        <div className="absolute top-1/2 left-1/4 w-[600px] h-[600px] bg-[#D4AF37]/5 rounded-full blur-[150px] pointer-events-none" />
        
        <div className="container-custom relative z-10">
          {/* Mission Statement */}
          <motion.div
            ref={missionRef}
            initial={{ opacity: 0, y: 40 }}
            animate={isMissionInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center mb-20"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 mb-8">
              <Film size={18} className="text-[#D4AF37]" />
              <span className="text-[#D4AF37] text-sm font-medium">Notre mission</span>
            </div>
            
            <h2 className="text-3xl md:text-5xl font-medium mb-8 leading-tight">
              Vehiculer un message
              <br />
              <span className="text-gradient">culturel fort</span>
            </h2>
            
            <p className="text-[#a1a1aa] text-lg md:text-xl leading-relaxed">
              Le cinema engage au service de la Jeunesse et de la planete.
            </p>
          </motion.div>

          {/* Engagements */}
          <motion.div
            ref={engagementsRef}
            initial={{ opacity: 0, y: 40 }}
            animate={isEngagementsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h3 className="text-center text-2xl font-medium text-white mb-4">
              Des projets en coherence avec
            </h3>
            <h3 className="text-center text-2xl font-medium text-gradient mb-12">
              nos engagements
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {engagements.map((engagement, index) => (
                <motion.div
                  key={engagement.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isEngagementsInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                  className="group relative p-6 rounded-2xl bg-[#0a0a0a] border border-neutral-800 hover:border-[#D4AF37]/30 transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <engagement.icon size={24} className="text-[#D4AF37]" />
                  </div>
                  <span className="text-[#D4AF37] text-sm font-medium block mb-3">/ {engagement.index}</span>
                  <h4 className="text-white text-xl font-medium mb-4">{engagement.title}</h4>
                  <p className="text-[#a1a1aa] text-sm leading-relaxed">{engagement.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Divider */}
      <div className="container-custom">
        <div className="h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
      </div>

      {/* Section 2: La Jeunesse */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        {/* Background */}
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-[#D4AF37]/5 rounded-full blur-[200px] pointer-events-none" />
        
        <div className="container-custom relative z-10">
          <motion.div
            ref={youthRef}
            initial={{ opacity: 0, y: 40 }}
            animate={isYouthInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center"
          >
            {/* Left: Content */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 mb-8">
                <Sparkles size={18} className="text-[#D4AF37]" />
                <span className="text-[#D4AF37] text-sm font-medium">Notre priorite</span>
              </div>
              
              <h2 className="text-3xl md:text-5xl font-medium mb-6 leading-tight">
                La jeunesse :
                <br />
                <span className="text-gradient">notre horizon</span>
              </h2>
              
              <p className="text-[#a1a1aa] text-lg leading-relaxed mb-8">
                Ils sont a la fois notre <span className="text-white font-medium">public</span> et nos <span className="text-white font-medium">createurs</span>.
                La jeunesse n'est pas l'avenir du cinema — elle en est le present le plus vibrant.
                Nous leur ouvrons les portes que d'autres leur ferment.
              </p>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                {youthStats.map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={isYouthInView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                    className="text-center p-4 rounded-xl bg-white/5 border border-white/10"
                  >
                    <p className="text-3xl font-bold text-[#D4AF37]">{stat.value}</p>
                    <p className="text-[#71717a] text-xs mt-1">{stat.label}</p>
                  </motion.div>
                ))}
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 rounded-xl bg-white/5">
                  <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                    <Target size={20} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-1">80% de nos seances pour les jeunes</h4>
                    <p className="text-[#71717a] text-sm">Projections scolaires, festivals jeunesse, ateliers d'education a l'image</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 rounded-xl bg-white/5">
                  <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                    <Film size={20} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-1">Acquisition de films de jeunes talents</h4>
                    <p className="text-[#71717a] text-sm">Courts et longs metrages de realisateurs et producteurs emergents, exclus des circuits traditionnels</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Visual */}
            <div className="relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={isYouthInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="relative"
              >
                {/* Main image */}
                <div className="relative rounded-3xl overflow-hidden aspect-[4/5]">
                  <img
                    src="https://customer-assets.emergentagent.com/job_cinema-mission/artifacts/jhmehg1y_DISPOSITIF-JEUNES-AMBASSADEURS_CINEMA_REGION-SUD_LE-PERIL-JEUNE_KLAPISCH-1200.jpg"
                    alt="Le Peril Jeune - Cedric Klapisch"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
                </div>
                
                {/* Decorative elements */}
                <div className="absolute -top-4 -right-4 w-24 h-24 border-2 border-[#D4AF37]/20 rounded-2xl" />
                <div className="absolute top-1/2 -right-8 w-16 h-16 bg-[#D4AF37]/10 rounded-full blur-xl" />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 relative overflow-hidden bg-gradient-to-b from-[#050505] via-[#0a0a0a] to-[#050505]">
        <div className="container-custom relative z-10">
          <motion.div
            ref={manifestoRef}
            initial={{ opacity: 0, y: 40 }}
            animate={isManifestoInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="/faire-un-don"
                className="btn-primary flex items-center gap-2"
              >
                Rejoignez notre mouvement
                <ArrowRight size={18} />
              </a>
              <a
                href="/projets"
                className="px-6 py-3 rounded-full border border-white/20 text-white hover:bg-white/5 transition-colors"
              >
                Decouvrir nos projets
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
